import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, Mail, User, Upload, Download, FileSpreadsheet, 
  Pencil, Trash2, Search, SortAsc, ArrowUpDown, FileDown, Check, AlertCircle,
  LayoutGrid, LayoutList, Calendar, Building2, Briefcase, CheckCircle2,
  Users, Send, Loader2
} from 'lucide-react';
import { validateCPF, maskCPF, cleanCPF, maskPhone } from '../utils/brazilValidation';

// Extracted employee components
import { EmployeeStatsCards, EmployeeEmptyState } from '../components/employees';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [openExport, setOpenExport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [exportStatus, setExportStatus] = useState('');
  const [cpfError, setCpfError] = useState('');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const fileInputRef = useRef(null);
  
  // Bulk Invite States
  const [openBulkInvite, setOpenBulkInvite] = useState(false);
  const [bulkInviteMode, setBulkInviteMode] = useState('not_accessed'); // 'not_accessed' or 'by_date'
  const [bulkInviteDateStart, setBulkInviteDateStart] = useState('');
  const [bulkInviteDateEnd, setBulkInviteDateEnd] = useState('');
  const [bulkInviteProgress, setBulkInviteProgress] = useState({ sending: false, current: 0, total: 0, success: 0, failed: 0 });
  
  const emptyForm = {
    nome: '',
    cargo: '',
    setor: '',
    sexo: '',
    data_admissao: '',
    email: '',
    cpf: '',
    codigo_esocial: '',
    whatsapp: '',
    status: 'ativo',
    data_demissao: '',
    data_saida: '',
    data_retorno: '',
    cid: '',
    observacoes: ''
  };
  
  const [formData, setFormData] = useState(emptyForm);

  // Handle CPF input with mask and validation
  const handleCPFChange = (value) => {
    const masked = maskCPF(value);
    setFormData({ ...formData, cpf: masked });
    
    // Validate when complete
    const cleaned = cleanCPF(value);
    if (cleaned.length === 11) {
      if (!validateCPF(cleaned)) {
        setCpfError('CPF inválido');
      } else {
        setCpfError('');
      }
    } else if (cleaned.length > 0) {
      setCpfError('');
    }
  };

  // Handle WhatsApp input with mask
  const handleWhatsAppChange = (value) => {
    const masked = maskPhone(value);
    setFormData({ ...formData, whatsapp: masked });
  };

  useEffect(() => {
    fetchEmployees();
    
    // Listen for company changes to refresh data
    const handleCompanyChange = () => {
      setLoading(true);
      fetchEmployees();
    };
    
    window.addEventListener('companyChanged', handleCompanyChange);
    
    return () => {
      window.removeEventListener('companyChanged', handleCompanyChange);
    };
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/employees`);
      setEmployees(response.data);
    } catch (error) {
      toast.error('Erro ao carregar funcionários');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditMode(false);
    setEditingId(null);
    setCpfError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate CPF if provided (required for e-Social)
    if (formData.cpf && cleanCPF(formData.cpf).length > 0) {
      if (cleanCPF(formData.cpf).length !== 11) {
        toast.error('CPF incompleto. Digite os 11 dígitos.');
        return;
      }
      if (!validateCPF(formData.cpf)) {
        toast.error('CPF inválido. Verifique os dígitos.');
        return;
      }
    }
    
    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        // Ensure sexo is sent correctly
        sexo: formData.sexo || ''
      };
      
      if (editMode && editingId) {
        await axios.put(`${API_URL}/employees/${editingId}`, submitData);
        toast.success('Funcionário atualizado com sucesso!');
      } else {
        await axios.post(`${API_URL}/employees`, submitData);
        toast.success('Funcionário cadastrado com sucesso!');
      }
      setOpen(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (editMode ? 'Erro ao atualizar funcionário' : 'Erro ao cadastrar funcionário');
      toast.error(errorMsg);
      console.error('Employee submit error:', error.response?.data);
    }
  };

  const handleEdit = (employee) => {
    setFormData({
      nome: employee.nome || '',
      cargo: employee.cargo || '',
      setor: employee.setor || '',
      sexo: employee.sexo || '',
      data_admissao: employee.data_admissao || '',
      email: employee.email || '',
      cpf: employee.cpf || '',
      codigo_esocial: employee.codigo_esocial || '',
      whatsapp: employee.whatsapp || '',
      status: employee.status || 'ativo',
      data_demissao: employee.data_demissao || '',
      data_saida: employee.data_saida || '',
      data_retorno: employee.data_retorno || '',
      cid: employee.cid || '',
      observacoes: employee.observacoes || ''
    });
    setEditMode(true);
    setEditingId(employee.id);
    setOpen(true);
  };

  const handleDelete = async (employeeId, employeeName) => {
    if (!window.confirm(`Tem certeza que deseja excluir ${employeeName}?`)) return;
    try {
      await axios.delete(`${API_URL}/employees/${employeeId}`);
      toast.success('Funcionário excluído com sucesso!');
      fetchEmployees();
    } catch (error) {
      toast.error('Erro ao excluir funcionário');
    }
  };

  const handleSendInvite = async (employeeId, employeeName = '') => {
    try {
      console.log(`[CONVITE TOKEN] Enviando convite para funcionário ${employeeId}...`);
      
      const response = await axios.post(`${API_URL}/employees/${employeeId}/send-invite`);
      const data = response.data;
      
      // Debug logs removed for production
      if (process.env.NODE_ENV === 'development' && data.email_result) {
        console.debug('[DEV] Email invite result:', data.email_result.status);
      }
      
      if (data.email_sent) {
        if (data.already_active) {
          toast.success(
            <div>
              <strong>Lembrete enviado!</strong>
              <p className="text-sm">O funcionário já possui conta ativa.</p>
            </div>
          );
        } else if (data.is_resend) {
          toast.success(
            <div>
              <strong>Novo convite enviado!</strong>
              <p className="text-sm">O convite anterior foi invalidado.</p>
              <p className="text-xs text-slate-500">Válido por {data.expires_in_hours}h</p>
            </div>
          );
        } else {
          toast.success(
            <div>
              <strong>Convite enviado com sucesso!</strong>
              <p className="text-sm">O funcionário receberá um link para ativar a conta.</p>
              <p className="text-xs text-slate-500">Válido por {data.expires_in_hours}h</p>
            </div>
          );
        }
      } else {
        toast.error(
          <div>
            <strong>{data.message || 'Erro ao enviar convite'}</strong>
            <p className="text-xs">Verifique o console (F12) para detalhes.</p>
          </div>,
          { duration: 8000 }
        );
      }
      
      fetchEmployees();
    } catch (error) {
      console.error('[CONVITE TOKEN ERRO] Falha ao enviar convite:', error);
      if (error.response?.data) {
        console.error('[CONVITE TOKEN ERRO] Dados da resposta:', error.response.data);
      }
      
      const message = error.response?.data?.detail || 'Erro ao enviar convite';
      toast.error(
        <div>
          <strong>{message}</strong>
          <p className="text-xs">Verifique o console (F12) para detalhes.</p>
        </div>
      );
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/employees/template`);
      const { content, filename } = response.data;
      
      const byteCharacters = atob(content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template baixado com sucesso!');
    } catch (error) {
      toast.error('Erro ao baixar template');
    }
  };

  const exportEmployees = async (status = '') => {
    setExporting(true);
    try {
      const url = status 
        ? `${API_URL}/employees/export?status=${status}`
        : `${API_URL}/employees/export`;
      
      const response = await axios.get(url);
      const { content, filename, total } = response.data;
      
      const byteCharacters = atob(content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Planilha gerada com ${total} funcionário(s)!`);
      setOpenExport(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao exportar funcionários');
    } finally {
      setExporting(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/employees/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const { imported, errors } = response.data;
      
      if (errors && errors.length > 0) {
        toast.warning(`${imported} funcionários importados. ${errors.length} erros encontrados.`);
      } else {
        toast.success(`${imported} funcionários importados com sucesso!`);
      }
      
      setOpenImport(false);
      fetchEmployees();
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar funcionários');
    } finally {
      setImporting(false);
    }
  };

  const getStatusBadge = (status, variant = 'default') => {
    const badges = {
      ativo: variant === 'card' 
        ? <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>Ativo
          </span>
        : <Badge className="bg-emerald-500">Ativo</Badge>,
      inativo: variant === 'card'
        ? <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-yellow-50 text-yellow-700 border border-yellow-200">
            <span className="w-1.5 h-1.5 rounded-full bg-yellow-500"></span>Inativo
          </span>
        : <Badge className="bg-yellow-500">Inativo</Badge>,
      demitido: variant === 'card'
        ? <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>Demitido
          </span>
        : <Badge className="bg-red-500">Demitido</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  // Bulk Invite Function
  const handleBulkInvite = async () => {
    // Filter employees based on selected mode
    let targetEmployees = employees.filter(emp => emp.status === 'ativo');
    
    if (bulkInviteMode === 'not_accessed') {
      // Only employees who haven't accessed (no user_id or never logged in)
      targetEmployees = targetEmployees.filter(emp => !emp.user_id || !emp.ultimo_acesso);
    } else if (bulkInviteMode === 'by_date') {
      // Filter by registration date
      if (bulkInviteDateStart) {
        targetEmployees = targetEmployees.filter(emp => {
          const admDate = new Date(emp.data_admissao || emp.created_at);
          return admDate >= new Date(bulkInviteDateStart);
        });
      }
      if (bulkInviteDateEnd) {
        targetEmployees = targetEmployees.filter(emp => {
          const admDate = new Date(emp.data_admissao || emp.created_at);
          return admDate <= new Date(bulkInviteDateEnd);
        });
      }
    }
    
    if (targetEmployees.length === 0) {
      toast.warning('Nenhum funcionário encontrado com os filtros selecionados.');
      return;
    }
    
    setBulkInviteProgress({ sending: true, current: 0, total: targetEmployees.length, success: 0, failed: 0 });
    
    let success = 0;
    let failed = 0;
    
    for (let i = 0; i < targetEmployees.length; i++) {
      const emp = targetEmployees[i];
      setBulkInviteProgress(prev => ({ ...prev, current: i + 1 }));
      
      try {
        await axios.post(`${API_URL}/employees/${emp.id}/send-invite`);
        success++;
        setBulkInviteProgress(prev => ({ ...prev, success }));
      } catch (error) {
        failed++;
        setBulkInviteProgress(prev => ({ ...prev, failed }));
        console.error(`Erro ao enviar convite para ${emp.nome}:`, error);
      }
      
      // Small delay to avoid overwhelming the server
      if (i < targetEmployees.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
    
    setBulkInviteProgress(prev => ({ ...prev, sending: false }));
    
    if (success > 0) {
      toast.success(`${success} convite(s) enviado(s) com sucesso!`);
    }
    if (failed > 0) {
      toast.error(`${failed} convite(s) falharam.`);
    }
    
    fetchEmployees();
    setOpenBulkInvite(false);
    setBulkInviteProgress({ sending: false, current: 0, total: 0, success: 0, failed: 0 });
  };

  // Filter and sort employees
  const filteredEmployees = employees
    .filter(emp => 
      emp.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.cargo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.setor?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const nameA = (a.nome || '').toLowerCase();
      const nameB = (b.nome || '').toLowerCase();
      return sortOrder === 'asc' 
        ? nameA.localeCompare(nameB, 'pt-BR')
        : nameB.localeCompare(nameA, 'pt-BR');
    });

  const toggleSort = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-[#e8f4fc] rounded-full"></div>
            <div className="w-16 h-16 border-4 border-[#0d9488] border-t-transparent rounded-full animate-spin absolute top-0"></div>
          </div>
          <span className="text-[#64748b] font-medium">Carregando funcionários...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#1e3a5f]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Gestão de Funcionários
          </h2>
          <p className="text-[#64748b] mt-1">
            Cadastre e gerencie os funcionários da empresa
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Export Dialog */}
          <Dialog open={openExport} onOpenChange={setOpenExport}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-[#0d9488] text-[#0d9488] hover:bg-[#e8f4fc]">
                <FileDown size={18} className="mr-2" />
                Exportar Excel
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Exportar Funcionários para Excel</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-slate-600">Selecione o filtro para exportar:</p>
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    onClick={() => exportEmployees('')} 
                    variant="outline" 
                    className="h-20 flex-col"
                    disabled={exporting}
                  >
                    <User size={24} className="mb-2" />
                    <span>Todos</span>
                  </Button>
                  <Button 
                    onClick={() => exportEmployees('ativo')} 
                    variant="outline" 
                    className="h-20 flex-col border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                    disabled={exporting}
                  >
                    <User size={24} className="mb-2" />
                    <span>Ativos</span>
                  </Button>
                  <Button 
                    onClick={() => exportEmployees('inativo')} 
                    variant="outline" 
                    className="h-20 flex-col border-yellow-300 text-yellow-600 hover:bg-yellow-50"
                    disabled={exporting}
                  >
                    <User size={24} className="mb-2" />
                    <span>Inativos</span>
                  </Button>
                  <Button 
                    onClick={() => exportEmployees('demitido')} 
                    variant="outline" 
                    className="h-20 flex-col border-red-300 text-red-600 hover:bg-red-50"
                    disabled={exporting}
                  >
                    <User size={24} className="mb-2" />
                    <span>Demitidos</span>
                  </Button>
                </div>
                {exporting && (
                  <p className="text-center text-sm text-slate-500">Gerando planilha...</p>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={openImport} onOpenChange={setOpenImport}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#e8f4fc]">
                <Upload size={18} className="mr-2" />
                Importar Excel
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Importar Funcionários via Excel</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800 dark:text-blue-200 mb-3"><strong>Instruções:</strong></p>
                  <ol className="text-sm text-blue-700 dark:text-blue-300 space-y-1 list-decimal list-inside">
                    <li>Baixe o template Excel</li>
                    <li>Preencha todos os campos obrigatórios</li>
                    <li>Data deve estar no formato DD/MM/AAAA</li>
                    <li>Faça o upload do arquivo preenchido</li>
                  </ol>
                </div>
                
                <Button onClick={downloadTemplate} variant="outline" className="w-full">
                  <Download size={20} className="mr-2" />
                  Baixar Template Excel
                </Button>

                <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                  <FileSpreadsheet size={48} className="mx-auto text-slate-400 mb-4" />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <Button asChild disabled={importing}>
                      <span>{importing ? 'Importando...' : 'Selecionar Arquivo Excel'}</span>
                    </Button>
                  </label>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Bulk Invite Dialog */}
          <Dialog open={openBulkInvite} onOpenChange={setOpenBulkInvite}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-[#0d9488] text-[#0d9488] hover:bg-[#e8f4fc]">
                <Users size={18} className="mr-2" />
                Convidar em Massa
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Send size={20} className="text-[#0d9488]" />
                  Convidar Funcionários em Massa
                </DialogTitle>
              </DialogHeader>
              
              {!bulkInviteProgress.sending ? (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Envie convites de acesso para múltiplos funcionários de uma vez.
                  </p>
                  
                  {/* Filter Options */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium">Selecione o filtro:</label>
                    
                    <div 
                      onClick={() => setBulkInviteMode('not_accessed')}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        bulkInviteMode === 'not_accessed' 
                          ? 'border-[#0d9488] bg-[#e8f4fc]' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          bulkInviteMode === 'not_accessed' ? 'border-[#0d9488]' : 'border-slate-300'
                        }`}>
                          {bulkInviteMode === 'not_accessed' && (
                            <div className="w-2 h-2 rounded-full bg-[#0d9488]" />
                          )}
                        </div>
                        <span className="font-medium text-sm">Funcionários que ainda não acessaram</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 ml-6">
                        Envia convite apenas para quem nunca fez login no sistema
                      </p>
                    </div>
                    
                    <div 
                      onClick={() => setBulkInviteMode('by_date')}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        bulkInviteMode === 'by_date' 
                          ? 'border-[#0d9488] bg-[#e8f4fc]' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          bulkInviteMode === 'by_date' ? 'border-[#0d9488]' : 'border-slate-300'
                        }`}>
                          {bulkInviteMode === 'by_date' && (
                            <div className="w-2 h-2 rounded-full bg-[#0d9488]" />
                          )}
                        </div>
                        <span className="font-medium text-sm">Por data de cadastro</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 ml-6">
                        Filtra funcionários pela data de admissão/cadastro
                      </p>
                    </div>
                    
                    {/* Date Filter Fields */}
                    {bulkInviteMode === 'by_date' && (
                      <div className="grid grid-cols-2 gap-3 mt-3 p-3 bg-slate-50 rounded-lg">
                        <div>
                          <Label className="text-xs">Data Inicial</Label>
                          <Input
                            type="date"
                            value={bulkInviteDateStart}
                            onChange={(e) => setBulkInviteDateStart(e.target.value)}
                            className="h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Data Final</Label>
                          <Input
                            type="date"
                            value={bulkInviteDateEnd}
                            onChange={(e) => setBulkInviteDateEnd(e.target.value)}
                            className="h-9"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                    <p className="text-xs text-amber-800">
                      <strong>Nota:</strong> Os convites serão enviados por e-mail (atendimento@mail.cerpbrasil.com.br) 
                      e WhatsApp (se o número estiver cadastrado).
                    </p>
                  </div>
                  
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setOpenBulkInvite(false)}>
                      Cancelar
                    </Button>
                    <Button 
                      onClick={handleBulkInvite}
                      className="bg-[#0d9488] hover:bg-[#0b7d73]"
                    >
                      <Send size={16} className="mr-2" />
                      Enviar Convites
                    </Button>
                  </DialogFooter>
                </div>
              ) : (
                /* Progress View */
                <div className="py-6 space-y-4">
                  <div className="text-center">
                    <Loader2 size={40} className="mx-auto text-[#0d9488] animate-spin mb-4" />
                    <p className="font-medium text-lg text-[#1e3a5f]">
                      Enviando convites...
                    </p>
                    <p className="text-sm text-slate-600 mt-1">
                      {bulkInviteProgress.current} de {bulkInviteProgress.total} funcionários
                    </p>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-slate-200 rounded-full h-3">
                    <div 
                      className="bg-[#0d9488] h-3 rounded-full transition-all duration-300"
                      style={{ width: `${(bulkInviteProgress.current / bulkInviteProgress.total) * 100}%` }}
                    />
                  </div>
                  
                  {/* Stats */}
                  <div className="flex justify-center gap-6 text-sm">
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 size={16} className="text-emerald-500" />
                      <span className="text-emerald-600 font-medium">{bulkInviteProgress.success} enviados</span>
                    </div>
                    {bulkInviteProgress.failed > 0 && (
                      <div className="flex items-center gap-1.5">
                        <AlertCircle size={16} className="text-red-500" />
                        <span className="text-red-600 font-medium">{bulkInviteProgress.failed} falhas</span>
                      </div>
                    )}
                  </div>
                  
                  <p className="text-xs text-center text-slate-500">
                    Por favor, aguarde. Não feche esta janela.
                  </p>
                </div>
              )}
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#0d9488] hover:bg-[#0b7d73] text-white shadow-lg shadow-[#0d9488]/30" data-testid="add-employee-button">
                <Plus size={18} className="mr-2" />
                Novo Funcionário
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editMode ? 'Editar Funcionário' : 'Cadastrar Funcionário'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nome">Nome Completo *</Label>
                    <Input
                      id="nome"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cpf">CPF <span className="text-xs text-gray-500">(Obrigatório para e-Social)</span></Label>
                    <div className="relative">
                      <Input
                        id="cpf"
                        value={formData.cpf}
                        onChange={(e) => handleCPFChange(e.target.value)}
                        placeholder="000.000.000-00"
                        className={cpfError ? 'border-red-500 focus:ring-red-500 pr-10' : 'pr-10'}
                        maxLength={14}
                      />
                      {cleanCPF(formData.cpf).length === 11 && !cpfError && (
                        <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                      )}
                    </div>
                    {cpfError && (
                      <p className="text-sm text-red-500 flex items-center gap-1 mt-1">
                        <AlertCircle className="h-3 w-3" />
                        {cpfError}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="cargo">Cargo *</Label>
                    <Input
                      id="cargo"
                      value={formData.cargo}
                      onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="setor">Setor *</Label>
                    <Input
                      id="setor"
                      value={formData.setor}
                      onChange={(e) => setFormData({ ...formData, setor: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="sexo">Sexo</Label>
                    <Select
                      value={formData.sexo}
                      onValueChange={(value) => setFormData({ ...formData, sexo: value })}
                    >
                      <SelectTrigger id="sexo">
                        <SelectValue placeholder="Selecione o sexo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="feminino">Feminino</SelectItem>
                        <SelectItem value="masculino">Masculino</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="codigo_esocial">Código e-Social</Label>
                    <Input
                      id="codigo_esocial"
                      value={formData.codigo_esocial}
                      onChange={(e) => setFormData({ ...formData, codigo_esocial: e.target.value })}
                      placeholder="Código do e-Social"
                    />
                  </div>
                  <div>
                    <Label htmlFor="whatsapp">WhatsApp</Label>
                    <Input
                      id="whatsapp"
                      value={formData.whatsapp}
                      onChange={(e) => handleWhatsAppChange(e.target.value)}
                      placeholder="(00) 00000-0000"
                      maxLength={15}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="data_admissao">Data de Admissão *</Label>
                    <Input
                      id="data_admissao"
                      type="date"
                      value={formData.data_admissao}
                      onChange={(e) => setFormData({ ...formData, data_admissao: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="status">Status *</Label>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      required
                    >
                      <option value="ativo">Ativo</option>
                      <option value="inativo">Inativo</option>
                      <option value="demitido">Demitido</option>
                    </select>
                  </div>
                </div>

                {formData.status === 'demitido' && (
                  <div>
                    <Label htmlFor="data_demissao">Data de Demissão</Label>
                    <Input
                      id="data_demissao"
                      type="date"
                      value={formData.data_demissao}
                      onChange={(e) => setFormData({ ...formData, data_demissao: e.target.value })}
                    />
                  </div>
                )}

                {formData.status === 'inativo' && (
                  <div className="space-y-4 p-4 bg-yellow-50 dark:bg-yellow-950 rounded-lg border border-yellow-200">
                    <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-200">Informações de Afastamento</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="data_saida">Data de Saída</Label>
                        <Input
                          id="data_saida"
                          type="date"
                          value={formData.data_saida}
                          onChange={(e) => setFormData({ ...formData, data_saida: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="data_retorno">Data de Retorno Prevista</Label>
                        <Input
                          id="data_retorno"
                          type="date"
                          value={formData.data_retorno}
                          onChange={(e) => setFormData({ ...formData, data_retorno: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="cid">CID (Classificação Internacional de Doenças)</Label>
                      <Input
                        id="cid"
                        value={formData.cid}
                        onChange={(e) => setFormData({ ...formData, cid: e.target.value })}
                        placeholder="Ex: M54.5"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="observacoes">Observações</Label>
                  <Textarea
                    id="observacoes"
                    value={formData.observacoes}
                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                    rows={3}
                    placeholder="Informações adicionais..."
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setOpen(false); resetForm(); }}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600">
                    {editMode ? 'Salvar Alterações' : 'Cadastrar'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <EmployeeStatsCards employees={employees} />

      {/* Search and Stats */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="relative w-full md:w-96">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
          <Input
            placeholder="Buscar por nome, cargo ou setor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-[#e2e8f0] focus:border-[#0d9488] focus:ring-[#0d9488] bg-white"
          />
        </div>
        <div className="flex items-center gap-4 text-sm text-[#64748b]">
          <span className="font-medium">{filteredEmployees.length} funcionário(s)</span>
          
          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-[#f1f5f9] rounded-lg p-1">
            <button 
              onClick={() => setViewMode('cards')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'cards' ? 'bg-white shadow-sm text-[#0d9488]' : 'text-[#64748b] hover:text-[#1e3a5f]'}`}
              title="Visualização em cards"
            >
              <LayoutGrid size={18} />
            </button>
            <button 
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm text-[#0d9488]' : 'text-[#64748b] hover:text-[#1e3a5f]'}`}
              title="Visualização em tabela"
            >
              <LayoutList size={18} />
            </button>
          </div>
          
          <Button variant="ghost" size="sm" onClick={toggleSort} className="flex items-center gap-1 text-[#64748b] hover:text-[#1e3a5f] hover:bg-[#e8f4fc]">
            <ArrowUpDown size={16} />
            {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
          </Button>
        </div>
      </div>

      {/* Empty State */}
      {filteredEmployees.length === 0 && !loading && (
        <EmployeeEmptyState onAddClick={() => setOpen(true)} />
      )}

      {/* Employees List - Cards View */}
      {viewMode === 'cards' && filteredEmployees.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEmployees.map((employee) => (
            <div 
              key={employee.id}
              className="bg-white rounded-2xl p-5 shadow-[0_4px_20px_rgba(30,58,95,0.05)] border border-[rgba(30,58,95,0.05)] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group"
              data-testid={`employee-card-${employee.id}`}
            >
              <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#1e3a5f] to-[#0d9488] flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-105 transition-transform">
                  <span className="text-xl font-bold text-white">
                    {employee.nome?.charAt(0).toUpperCase()}
                  </span>
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-[#1e3a5f] truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {employee.nome}
                  </h3>
                  <p className="text-sm text-[#64748b] truncate">{employee.email}</p>
                  
                  <div className="mt-3 space-y-1.5">
                    <div className="flex items-center gap-2 text-sm text-[#64748b]">
                      <Briefcase size={14} className="text-[#94a3b8]" />
                      <span className="truncate">{employee.cargo || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#64748b]">
                      <Building2 size={14} className="text-[#94a3b8]" />
                      <span className="truncate">{employee.setor || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#64748b]">
                      <Calendar size={14} className="text-[#94a3b8]" />
                      <span>{employee.data_admissao ? new Date(employee.data_admissao).toLocaleDateString('pt-BR') : '-'}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Footer */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#f1f5f9]">
                {getStatusBadge(employee.status, 'card')}
                
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEdit(employee)}
                    className="h-8 w-8 p-0 text-[#64748b] hover:text-[#0d9488] hover:bg-[#e8f4fc]"
                    title="Editar"
                    data-testid={`edit-employee-${employee.id}`}
                  >
                    <Pencil size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(employee.id, employee.nome)}
                    className="h-8 w-8 p-0 text-[#64748b] hover:text-red-500 hover:bg-red-50"
                    title="Excluir"
                  >
                    <Trash2 size={14} />
                  </Button>
                  {employee.status === 'ativo' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleSendInvite(employee.id)}
                      className="h-8 w-8 p-0 text-[#64748b] hover:text-[#0d9488] hover:bg-[#e8f4fc]"
                      title={employee.convite_enviado ? "Reenviar Acesso" : "Enviar Acesso"}
                      data-testid={`send-access-${employee.id}`}
                    >
                      <Mail size={14} />
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Invite Status Badge */}
              {employee.convite_enviado && (
                <div className="mt-3 flex items-center justify-center">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[#e8f4fc] text-[#0d9488] border border-[#0d9488]/20">
                    <CheckCircle2 size={12} />
                    Convite enviado
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Employees List - Table View */}
      {viewMode === 'table' && (
      <Card className="overflow-hidden rounded-2xl border-0 shadow-[0_4px_20px_rgba(30,58,95,0.05)]">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#64748b] tracking-wider">
                  Nome
                </th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#64748b] tracking-wider">
                  Cargo
                </th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#64748b] tracking-wider">
                  Setor
                </th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#64748b] tracking-wider">
                  Admissão
                </th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#64748b] tracking-wider">
                  Status
                </th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#64748b] tracking-wider min-w-[200px]">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f1f5f9]">
              {filteredEmployees.map((employee) => (
                <tr 
                  key={employee.id} 
                  className="hover:bg-[#f8fafc] transition-colors"
                  data-testid={`employee-row-${employee.id}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="bg-gradient-to-br from-[#1e3a5f] to-[#0d9488] w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-white">
                          {employee.nome?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-[#1e3a5f] text-sm truncate">{employee.nome}</p>
                        <p className="text-xs text-[#94a3b8] truncate">{employee.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#64748b]">
                    {employee.cargo}
                  </td>
                  <td className="px-4 py-3 text-sm text-[#64748b]">
                    {employee.setor}
                  </td>
                  <td className="px-4 py-3 text-sm text-[#64748b]">
                    {employee.data_admissao ? new Date(employee.data_admissao).toLocaleDateString('pt-BR') : '-'}
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(employee.status)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 flex-nowrap">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(employee)}
                        className="text-[#0d9488] hover:text-[#0b7d73] hover:bg-[#e8f4fc] h-8 w-8 p-0"
                        title="Editar"
                        data-testid={`edit-employee-${employee.id}`}
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(employee.id, employee.nome)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                        title="Excluir"
                      >
                        <Trash2 size={14} />
                      </Button>
                      {employee.status === 'ativo' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSendInvite(employee.id)}
                          className="text-[#0d9488] hover:text-[#0b7d73] hover:bg-[#e8f4fc] h-8 px-2"
                          title={employee.convite_enviado ? "Reenviar Acesso" : "Enviar Acesso"}
                        >
                          <Mail size={14} />
                          <span className="ml-1 text-xs hidden sm:inline">{employee.convite_enviado ? "Reenviar" : "Acesso"}</span>
                        </Button>
                      )}
                      {employee.convite_enviado && (
                        <Badge variant="outline" className="text-[#0d9488] border-[#0d9488]/30 text-[10px] bg-[#e8f4fc] whitespace-nowrap">
                          Enviado
                        </Badge>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredEmployees.length === 0 && (
          <div className="p-12 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#e8f4fc] flex items-center justify-center mb-4">
              <User size={32} className="text-[#94a3b8]" />
            </div>
            <p className="text-[#64748b]">
              {searchTerm 
                ? 'Nenhum funcionário encontrado para esta busca.' 
                : 'Nenhum funcionário cadastrado ainda.'}
            </p>
          </div>
        )}
      </Card>
      )}
    </div>
  );
};

export default Employees;
